import { Autocomplete, Box, TextField, createFilterOptions } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useField } from 'formik';
import { useId } from 'react';

import { FormField, api, getQueryProps, isDefined, isTruthy, useLoggedInAuthContext } from 'common';

export const CREATE_AGENT_ID = '__CREATE_AGENT_ID__';

export type AgentIdControlVariant = 'PickAgent' | 'CreateOrPickAgent';
type AgentAutocompleteOption = api.FlowAgent.Agent | string;

const AGENT_AUTOCOMPLETE_FILTER = createFilterOptions<AgentAutocompleteOption>();

interface Props {
  name: string;
  label: string;
  /**
   * We restrict picking agents that are in a different organization for security reasons.
   * We will enforce this here and in the backend.
   */
  organizationId?: string;
  helperText?: string;
  disabled?: boolean;
  onAgentChange?: (args: { agentId: string | undefined; agentName?: string }) => void;
  variant?: AgentIdControlVariant;
  agentName?: string;
  agentNameErrorMessage?: string;
}

export function AgentIdControl(props: Props) {
  const {
    name,
    label,
    organizationId,
    helperText,
    disabled,
    onAgentChange,
    variant = 'PickAgent',
    agentName,
    agentNameErrorMessage,
  } = props;

  const { hasPermission } = useLoggedInAuthContext();
  const isFreeSolo = hasPermission.ModifyAgents && variant === 'CreateOrPickAgent';

  const { data = [], isLoading: isLoadingAgents } = useQuery({
    ...getQueryProps(api.client.FlowAgent, 'findAll', {
      organizationId,
    }),
    enabled: isTruthy(organizationId),
  });

  const { data: organizations = [], isLoading: isLoadingOrganizations } = useQuery({
    ...getQueryProps(api.client.Organization, 'findAll'),
  });

  const [field, { error }, { setValue }] = useField<string | undefined>(name);
  const fieldId = useId();

  const selectedValue =
    field.value === CREATE_AGENT_ID
      ? isTruthy(agentName)
        ? agentName
        : null
      : (data.find((agent) => agent.id === field.value) ?? null);

  const getOptionLabelWithOrganization = (agent: api.FlowAgent.Agent) => {
    let label = agent.name;
    // This depends on the organizationIds being passed in.
    // For example when editing a flow, we only want to show agents from the organization that the flow is in.
    // And we dont want to show the organization name in that case.
    if (!isDefined(organizationId) && organizations.length > 1) {
      const organizationName = organizations.find((org) => org.id === agent.organizationId)?.name;
      if (isDefined(organizationName)) {
        label += ` [${organizationName}]`;
      }
    }
    return label;
  };

  const setAgentValue = (agentId: string | undefined, agentName: string | undefined) => {
    void setValue(agentId);
    onAgentChange?.({ agentId, agentName });
  };

  return (
    <FormField
      id={fieldId}
      label={label}
      helperText={helperText ?? (isFreeSolo ? 'Type to search or add a new Agent.' : undefined)}
      error={error ?? agentNameErrorMessage}
    >
      <Autocomplete<AgentAutocompleteOption, false, false, boolean>
        id={fieldId}
        loading={isLoadingAgents || isLoadingOrganizations}
        {...field}
        options={data}
        value={selectedValue}
        disabled={disabled}
        multiple={false}
        freeSolo={isFreeSolo}
        clearOnBlur={isFreeSolo}
        filterOptions={(options, params) => {
          const filtered = AGENT_AUTOCOMPLETE_FILTER(options, params);
          const trimmedInputValue = params.inputValue.trim();
          if (isTruthy(trimmedInputValue)) {
            const matchesExistingAgent = data.some(
              (agent) =>
                agent.name.toLowerCase() === trimmedInputValue.toLowerCase() ||
                agent.id === trimmedInputValue
            );
            if (isFreeSolo && !matchesExistingAgent) {
              filtered.push(trimmedInputValue);
            }
          }
          return filtered;
        }}
        getOptionLabel={(agentOrId) => {
          if (typeof agentOrId === 'string') {
            const existingAgent = data.find((agent) => agent.id === agentOrId);
            if (isDefined(existingAgent)) {
              return getOptionLabelWithOrganization(existingAgent);
            }

            return agentOrId;
          }

          return getOptionLabelWithOrganization(agentOrId);
        }}
        onChange={(_, agentOrId) => {
          if (!isFreeSolo) {
            const agentId =
              typeof agentOrId === 'string' ? agentOrId : (agentOrId?.id ?? undefined);
            setAgentValue(agentId, '');
          } else if (typeof agentOrId === 'object' && isDefined(agentOrId)) {
            setAgentValue(agentOrId.id, '');
          } else if (typeof agentOrId === 'string' && isTruthy(agentOrId)) {
            const existingAgentById = data.find((agent) => agent.id === agentOrId);
            const existingAgentByName = data.find((agent) => agent.name === agentOrId);
            if (isDefined(existingAgentById)) {
              setAgentValue(existingAgentById.id, '');
            } else if (isDefined(existingAgentByName)) {
              setAgentValue(existingAgentByName.id, '');
            } else {
              setAgentValue(CREATE_AGENT_ID, agentOrId);
            }
          } else {
            setAgentValue(undefined, '');
          }
        }}
        renderInput={(params) => <TextField {...params} size="small" />}
        renderOption={(props, agentOrId) => {
          let optionLabel: string;
          let key: string;
          if (typeof agentOrId === 'string') {
            const existingAgent =
              data.find((agent) => agent.id === agentOrId) ??
              data.find((agent) => agent.name === agentOrId);
            if (isDefined(existingAgent)) {
              key = existingAgent.id;
              optionLabel = getOptionLabelWithOrganization(existingAgent);
            } else {
              key = CREATE_AGENT_ID;
              optionLabel = `Add "${agentOrId}"`;
            }
          } else {
            optionLabel = getOptionLabelWithOrganization(agentOrId);
            key = agentOrId.id;
          }
          return (
            <Box component="li" {...props} key={key}>
              {optionLabel}
            </Box>
          );
        }}
        sx={{
          minWidth: '320px',
        }}
      />
    </FormField>
  );
}
